import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AlertTriangle, CheckCircle, XCircle, Clock, Filter, Eye, Check, X, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { PageSkeleton, AlertItemSkeleton } from '../components/SkeletonLoader';
import { NoAlertsFound } from '../components/EmptyState';
import { formatAlertTime } from '../utils/dateTimeUtils';

const AlertsPage = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [statusFilter, setStatusFilter] = useState('all'); // all, active, acknowledged, resolved
    const [severityFilter, setSeverityFilter] = useState('all'); // all, critical, high, medium, low
    const [deviceFilter, setDeviceFilter] = useState('all');
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [acknowledgeNote, setAcknowledgeNote] = useState('');
    const [resolveNote, setResolveNote] = useState('');
    const [isResolveMode, setIsResolveMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit] = useState(50);

    // Get current user to check permissions
    const { data: currentUser } = useQuery('current-user', () => apiService.getCurrentUser(), {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false
    });

    const canModifyAlerts = currentUser?.role !== 'viewer';

    // Fetch alerts
    const { data: alertsResponse, isLoading, refetch } = useQuery(
        ['alerts', statusFilter, severityFilter, deviceFilter, currentPage, pageLimit],
        async () => {
            const filters = {
                page: currentPage,
                limit: pageLimit
            };
            if (statusFilter !== 'all') filters.status = statusFilter;
            if (severityFilter !== 'all') filters.severity = severityFilter;
            if (deviceFilter !== 'all') filters.device_id = deviceFilter;

            const response = await apiService.getAlerts(filters);
            return response;
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            keepPreviousData: true
        }
    );

    const alertsData = alertsResponse?.alerts || [];
    const pagination = alertsResponse?.pagination || {
        page: 1,
        limit: pageLimit,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false
    };



    // Fetch devices for filtering
    const { data: devices = [] } = useQuery(
        'devices',
        apiService.getDevices,
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            select: (data) => data?.devices || []
        }
    );

    // Fetch locations for timezone support
    const { data: locationsData } = useQuery(
        'locations',
        apiService.getLocations,
        {
            staleTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false
        }
    );

    const locations = locationsData?.locations || [];

    // Create a map of device_id to location for quick lookup
    const deviceLocationMap = React.useMemo(() => {
        const map = {};
        devices.forEach(device => {
            if (device.location_id) {
                const location = locations.find(l => l.id === device.location_id);
                if (location) {
                    map[device.id] = location;
                }
            }
        });
        return map;
    }, [devices, locations]);

    // Reset page when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, severityFilter, deviceFilter]);

    // Acknowledge alert mutation
    const acknowledgeMutation = useMutation(
        ({ alertId, note }) => apiService.acknowledgeAlert(alertId, note),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('alerts');
                setSelectedAlert(null);
                setAcknowledgeNote('');
                toast.success(t('alerts.acknowledgeSuccess', 'Alert acknowledged successfully'));
            },
            onError: () => {
                toast.error(t('alerts.acknowledgeError', 'Failed to acknowledge alert'));
            }
        }
    );

    // Resolve alert mutation
    const resolveMutation = useMutation(
        ({ alertId, note }) => apiService.resolveAlert(alertId, note),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('alerts');
                setSelectedAlert(null);
                setResolveNote('');
                setIsResolveMode(false);
                toast.success(t('alerts.resolveSuccess', 'Alert resolved successfully'));
            },
            onError: () => {
                toast.error(t('alerts.resolveError', 'Failed to resolve alert'));
            }
        }
    ); const getSeverityColor = (severity) => {
        const colors = {
            critical: 'bg-red-100 text-red-800 border-red-200',
            high: 'bg-orange-100 text-orange-800 border-orange-200',
            medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            low: 'bg-blue-100 text-blue-800 border-blue-200'
        };
        return colors[severity] || colors.medium;
    };

    const getStatusColor = (status) => {
        const colors = {
            active: 'bg-red-100 text-red-800',
            acknowledged: 'bg-yellow-100 text-yellow-800',
            resolved: 'bg-green-100 text-green-800'
        };
        return colors[status] || colors.active;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active': return <AlertTriangle className="h-4 w-4" />;
            case 'acknowledged': return <Clock className="h-4 w-4" />;
            case 'resolved': return <CheckCircle className="h-4 w-4" />;
            default: return <AlertTriangle className="h-4 w-4" />;
        }
    };

    const formatSeverityLabel = (severity) => {
        if (!severity) {
            return t('severity.low', 'Low');
        }
        const key = severity.toLowerCase();
        return t(`severity.${key}`, severity);
    };

    const alertTypeKeyMap = {
        threshold_crossing: 'threshold_crossing',
        threshold_exceeded: 'threshold_exceeded',
        threshold_below: 'threshold_below',
        above_max: 'threshold_exceeded',
        below_min: 'threshold_below',
        device_offline: 'device_offline',
        offline: 'offline',
        online: 'online',
        connection_lost: 'connection_lost',
        low_battery: 'low_battery',
        sensor_threshold: 'sensor_threshold',
        rule_violation: 'rule_violation',
        device_alarm: 'device_alarm',
        sensor_fault: 'sensor_fault'
    };

    const formatAlertTypeLabel = (type) => {
        if (!type) {
            return t('alerts.types.generic', 'Alert');
        }
        const translationKey = alertTypeKeyMap[type.toLowerCase()] || type.toLowerCase();
        return t(`alerts.types.${translationKey}`, type.replace(/_/g, ' '));
    };

    const getAlertValue = (alert) => {
        if (!alert) return null;
        if (alert.sensor_value !== undefined && alert.sensor_value !== null) return alert.sensor_value;
        if (alert.value !== undefined && alert.value !== null) return alert.value;
        if (alert.threshold_value !== undefined && alert.threshold_value !== null) return alert.threshold_value;
        return null;
    };

    const formatAlertMessage = (alert) => {
        if (!alert) return t('alerts.noMessage', 'No message provided');
        const type = alert.alert_type?.toLowerCase();
        const message = alert.message;
        const sensorName = alert.sensor_name || formatAlertTypeLabel(alert.alert_type);
        const readingValue = getAlertValue(alert);

        if (type === 'threshold_crossing' && message) {
            const thresholdPattern = /^(?<sensor>.+?) (?<direction>exceeded maximum|fell below minimum) threshold \(value: (?<value>[-\d.]+)\)/i;
            const match = message.match(thresholdPattern);
            if (match?.groups) {
                const { sensor, direction, value } = match.groups;
                const key = direction.toLowerCase().includes('exceeded')
                    ? 'thresholdExceeded'
                    : 'thresholdBelow';
                return t(`alerts.alertMessages.${key}`, {
                    sensor: sensor.trim(),
                    value: value.trim()
                });
            }
        }

        if (type === 'threshold_exceeded' || type === 'above_max') {
            return t('alerts.alertMessages.thresholdExceeded', {
                sensor: sensorName,
                value: readingValue ?? '—'
            });
        }

        if (type === 'threshold_below' || type === 'below_min') {
            return t('alerts.alertMessages.thresholdBelow', {
                sensor: sensorName,
                value: readingValue ?? '—'
            });
        }

        if (type === 'offline' || type === 'device_offline') {
            return t('alerts.alertMessages.offline', '{{device}} has gone offline', {
                device: alert.device_name || t('alerts.unknownDevice', 'Unknown device')
            });
        }

        if (!message) {
            return t('alerts.noMessage', 'No message provided');
        }

        return message;
    };

    const formatDeviceName = (name) => name || t('alerts.unknownDevice', 'Unknown device');

    const formatDateTime = (date, deviceLocation) => {
        if (!date) return '—';
        // Get device's location timezone if available
        const timezone = deviceLocation?.timezone || 'UTC';
        return formatAlertTime(date, timezone);
    };

    // Stats based on current page data and total
    const stats = React.useMemo(() => {
        const apiStats = alertsResponse?.stats;

        if (apiStats) {
            // Use stats from API (global counts across all alerts)
            return {
                total: apiStats.total || 0,
                active: apiStats.active || 0,
                acknowledged: apiStats.acknowledged || 0,
                resolved: apiStats.resolved || 0,
                critical: apiStats.critical || 0
            };
        }

        // Fallback to current page calculations if stats not available
        return {
            total: pagination.total,
            active: alertsData.filter(a => a.status === 'active').length,
            acknowledged: alertsData.filter(a => a.status === 'acknowledged').length,
            resolved: alertsData.filter(a => a.status === 'resolved').length,
            critical: alertsData.filter(a => a.severity === 'critical').length
        };
    }, [alertsData, pagination.total, alertsResponse]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">
                    {t('alerts.title', 'Alerts')}
                </h1>
                <p className="text-gray-500 mt-1">
                    {t('alerts.subtitle', 'Monitor and manage system alerts')}
                </p>
            </div>

            {/* Stats Cards with Pastel Gradients */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-200 via-blue-300 to-indigo-300 p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="relative z-10">
                        <p className="text-sm font-medium text-indigo-900">{t('alerts.total', 'Total')}</p>
                        <p className="text-3xl font-bold mt-2 text-indigo-950">{stats.total}</p>
                    </div>
                    <AlertTriangle className="absolute right-4 top-4 h-12 w-12 text-indigo-400 opacity-30" />
                </div>
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-red-200 via-red-300 to-pink-300 p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="relative z-10">
                        <p className="text-sm font-medium text-red-900">{t('alerts.active', 'Active')}</p>
                        <p className="text-3xl font-bold mt-2 text-red-950">{stats.active}</p>
                    </div>
                    <XCircle className="absolute right-4 top-4 h-12 w-12 text-red-400 opacity-30" />
                </div>
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-yellow-200 via-amber-200 to-orange-200 p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="relative z-10">
                        <p className="text-sm font-medium text-yellow-900">{t('alerts.acknowledged', 'Acknowledged')}</p>
                        <p className="text-3xl font-bold mt-2 text-yellow-950">{stats.acknowledged}</p>
                    </div>
                    <Clock className="absolute right-4 top-4 h-12 w-12 text-yellow-400 opacity-30" />
                </div>
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-200 via-emerald-200 to-teal-300 p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="relative z-10">
                        <p className="text-sm font-medium text-green-900">{t('alerts.resolved', 'Resolved')}</p>
                        <p className="text-3xl font-bold mt-2 text-green-950">{stats.resolved}</p>
                    </div>
                    <CheckCircle className="absolute right-4 top-4 h-12 w-12 text-green-400 opacity-30" />
                </div>
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-200 via-amber-200 to-yellow-300 p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="relative z-10">
                        <p className="text-sm font-medium text-orange-900">{t('alerts.critical', 'Critical')}</p>
                        <p className="text-3xl font-bold mt-2 text-orange-950">{stats.critical}</p>
                    </div>
                    <AlertTriangle className="absolute right-4 top-4 h-12 w-12 text-orange-400 opacity-30" />
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Filter className="inline h-4 w-4 mr-1" />
                            {t('alerts.filterStatus', 'Status')}
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full rounded-md border-gray-300"
                        >
                            <option value="all">{t('alerts.allStatuses', 'All Statuses')}</option>
                            <option value="active">{t('alerts.active', 'Active')}</option>
                            <option value="acknowledged">{t('alerts.acknowledged', 'Acknowledged')}</option>
                            <option value="resolved">{t('alerts.resolved', 'Resolved')}</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('alerts.filterSeverity', 'Severity')}
                        </label>
                        <select
                            value={severityFilter}
                            onChange={(e) => setSeverityFilter(e.target.value)}
                            className="w-full rounded-md border-gray-300"
                        >
                            <option value="all">{t('alerts.allSeverities', 'All Severities')}</option>
                            <option value="critical">{t('severity.critical', 'Critical')}</option>
                            <option value="high">{t('severity.high', 'High')}</option>
                            <option value="medium">{t('severity.medium', 'Medium')}</option>
                            <option value="low">{t('severity.low', 'Low')}</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('alerts.filterDevice', 'Device')}
                        </label>
                        <select
                            value={deviceFilter}
                            onChange={(e) => setDeviceFilter(e.target.value)}
                            className="w-full rounded-md border-gray-300"
                        >
                            <option value="all">{t('alerts.allDevices', 'All Devices')}</option>
                            {devices.map(device => (
                                <option key={device.id} value={device.id}>
                                    {device.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Alerts Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {isLoading ? (
                    <div className="p-8 space-y-3">
                        <AlertItemSkeleton />
                        <AlertItemSkeleton />
                        <AlertItemSkeleton />
                    </div>
                ) : alertsData.length === 0 ? (
                    <NoAlertsFound
                        onClearFilters={() => {
                            setStatusFilter('all');
                            setSeverityFilter('all');
                            setDeviceFilter('all');
                        }}
                        hasFilters={statusFilter !== 'all' || severityFilter !== 'all' || deviceFilter !== 'all'}
                    />
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('alerts.severity', 'Severity')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('alerts.device', 'Device')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('alerts.message', 'Message')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('alerts.time', 'Time')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('alerts.status', 'Status')}
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common.actions', 'Actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {alertsData.map((alert) => {
                                const alertValue = getAlertValue(alert);
                                return (
                                    <tr key={alert.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getSeverityColor(alert.severity)}`}>
                                                {formatSeverityLabel(alert.severity)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {formatDeviceName(alert.device_name)}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {alert.sensor_name || formatAlertTypeLabel(alert.alert_type)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">{formatAlertMessage(alert)}</div>
                                            {alertValue !== null && (
                                                <div className="text-xs text-gray-500">
                                                    {t('alerts.valueLabel', 'Value: {{value}}', {
                                                        value: alertValue
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div>{formatDateTime(alert.created_at, deviceLocationMap[alert.device_id])}</div>
                                            {deviceLocationMap[alert.device_id]?.timezone && deviceLocationMap[alert.device_id].timezone !== 'UTC' && (
                                                <div className="text-xs text-gray-400 mt-0.5">
                                                    {deviceLocationMap[alert.device_id].timezone.replace(/_/g, ' ')}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col gap-1">
                                                <span className={`px-2 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${getStatusColor(alert.status)}`}>
                                                    {getStatusIcon(alert.status)}
                                                    {t(`alerts.statusLabels.${alert.status}`, alert.status)}
                                                </span>
                                                {alert.status === 'acknowledged' && alert.acknowledged_by_email && (
                                                    <div className="text-xs text-gray-500">
                                                        {t('alerts.by', 'by')} {alert.acknowledged_by_email.split('@')[0]}
                                                    </div>
                                                )}
                                                {alert.status === 'resolved' && alert.resolved_by_email && (
                                                    <div className="text-xs text-gray-500">
                                                        {t('alerts.by', 'by')} {alert.resolved_by_email.split('@')[0]}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                {(alert.status === 'active' || alert.status === 'acknowledged') && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedAlert(alert);
                                                            setIsResolveMode(true);
                                                            setResolveNote('');
                                                        }}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-100 text-green-700 text-xs font-medium hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title={canModifyAlerts ? t('alerts.resolve', 'Resolve') : t('alerts.viewerCannotModify', 'Viewers cannot modify alerts')}
                                                        disabled={!canModifyAlerts}
                                                    >
                                                        <CheckCircle className="h-3 w-3" />
                                                        {t('alerts.resolve', 'Resolve')}
                                                    </button>
                                                )}
                                                {alert.status === 'active' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (canModifyAlerts) {
                                                                setSelectedAlert(alert);
                                                                setIsResolveMode(false);
                                                                setAcknowledgeNote('');
                                                            }
                                                        }}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 text-xs font-medium hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title={canModifyAlerts ? t('alerts.acknowledge', 'Acknowledge') : t('alerts.viewerCannotModify', 'Viewers cannot modify alerts')}
                                                        disabled={!canModifyAlerts}
                                                    >
                                                        <Check className="h-3 w-3" />
                                                        {t('alerts.ack', 'Ack')}
                                                    </button>
                                                )}
                                                <Link
                                                    to={`/devices/${alert.device_id}`}
                                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 transition-colors"
                                                    title={t('alerts.viewDevice', 'View Device')}
                                                >
                                                    <Eye className="h-3 w-3" />
                                                    {t('alerts.view', 'View')}
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination Controls */}
            {!isLoading && alertsData.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-4 mt-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                            {t('common.showing', 'Showing')} <span className="font-medium">{((currentPage - 1) * pageLimit) + 1}</span> {t('common.to', 'to')} <span className="font-medium">{Math.min(currentPage * pageLimit, pagination.total)}</span> {t('common.of', 'of')} <span className="font-medium">{pagination.total}</span> {t('common.results', 'results')}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={!pagination.hasPrev}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${pagination.hasPrev
                                    ? 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
                                    }`}
                            >
                                {t('common.previous', 'Previous')}
                            </button>
                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (pagination.totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= pagination.totalPages - 2) {
                                        pageNum = pagination.totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNum
                                                ? 'text-white bg-indigo-600'
                                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                                disabled={!pagination.hasNext}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${pagination.hasNext
                                    ? 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
                                    }`}
                            >
                                {t('common.next', 'Next')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Acknowledge/Resolve Modal */}
            {selectedAlert && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setSelectedAlert(null)}
                >
                    <div
                        className="w-full max-w-md bg-white rounded-lg shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="border-b border-gray-200 p-6">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {isResolveMode
                                    ? t('alerts.resolveAlert', 'Resolve Alert')
                                    : t('alerts.acknowledgeAlert', 'Acknowledge Alert')}
                            </h3>
                        </div>

                        <div className="p-6">
                            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                <div className="text-sm font-medium text-gray-900">{formatAlertMessage(selectedAlert)}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {formatDeviceName(selectedAlert.device_name)} • {formatDateTime(selectedAlert.created_at)}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <MessageSquare className="inline h-4 w-4 mr-1" />
                                    {isResolveMode
                                        ? t('alerts.resolutionNote', 'Resolution Note (optional)')
                                        : t('alerts.addNote', 'Add Note (optional)')
                                    }
                                </label>
                                <textarea
                                    value={isResolveMode ? resolveNote : acknowledgeNote}
                                    onChange={(e) => isResolveMode ? setResolveNote(e.target.value) : setAcknowledgeNote(e.target.value)}
                                    className="w-full rounded-md border-gray-300"
                                    rows="3"
                                    placeholder={t('alerts.notePlaceholder', 'Describe the action taken...')}
                                />
                            </div>
                        </div>

                        <div className="border-t border-gray-200 p-6 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setSelectedAlert(null);
                                    setAcknowledgeNote('');
                                    setResolveNote('');
                                    setIsResolveMode(false);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            {canModifyAlerts && (
                                <button
                                    onClick={() => {
                                        if (isResolveMode) {
                                            resolveMutation.mutate({ alertId: selectedAlert.id, note: resolveNote });
                                        } else {
                                            acknowledgeMutation.mutate({
                                                alertId: selectedAlert.id,
                                                note: acknowledgeNote
                                            });
                                        }
                                    }}
                                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${isResolveMode
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-indigo-600 hover:bg-indigo-700'
                                        }`}
                                    disabled={acknowledgeMutation.isLoading || resolveMutation.isLoading}
                                >
                                    {isResolveMode
                                        ? t('alerts.resolve', 'Resolve')
                                        : t('alerts.acknowledge', 'Acknowledge')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlertsPage;
